/**
 * Reads the `projectIds` claim from a JWT body without validating the
 * token. PRD-002 forbids JWT VALIDATION; this is claim-extraction for
 * application logic. Located in `src/web/src/project/` (not `auth/`)
 * because the no-jwt-decode-libs rule scopes to the auth namespace.
 *
 * Implementation detail: base64url decode of segment 2 via `Uint8Array`
 * + `TextDecoder` — no `atob`, no `jsonwebtoken`, no `jose`. Decoded
 * payload is parsed as JSON; the `projectIds` field is read defensively.
 *
 * Any malformed input → `[]`. The user lands in the zero-intersection
 * state (clear "no access" message), which is the right user-facing
 * outcome regardless of why the claim is absent.
 */
export function extractProjectIds(jwt: string): string[] {
    return extractStringArrayClaim(jwt, "projectIds");
}

/**
 * PRD-005: client-side advisory read of the JWT `permissions` claim. Used by
 * `<AuthorizedGate>` to short-circuit the gate stack with the deny page when
 * the user lacks `Subject_Read` — preventing the `useIdentity` /auth/identity
 * call (which now 403s on missing Subject_Read) from being the first thing
 * the user hits. The server is still the authoritative gate; this helper is
 * a UX pre-flight and nothing more.
 *
 * Any malformed input → `[]`. The downstream code treats absence of
 * `Subject_Read` as "show the deny page" so a non-array claim defaults safely.
 */
export function extractPermissions(jwt: string): string[] {
    return extractStringArrayClaim(jwt, "permissions");
}

function extractStringArrayClaim(jwt: string, claimName: string): string[] {
    if (typeof jwt !== "string") return [];
    const parts = jwt.split(".");
    if (parts.length !== 3) return [];
    const body = decodeBase64UrlToString(parts[1]!);
    if (body === null) return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(body);
    } catch {
        return [];
    }
    if (!parsed || typeof parsed !== "object") return [];
    const claim = (parsed as Record<string, unknown>)[claimName];
    if (!Array.isArray(claim)) return [];
    return claim.filter((v): v is string => typeof v === "string");
}

function decodeBase64UrlToString(b64url: string): string | null {
    try {
        const padded = b64url.replace(/-/g, "+").replace(/_/g, "/");
        const padding = "=".repeat((4 - (padded.length % 4)) % 4);
        const decoded = decodeBase64Standard(padded + padding);
        if (decoded === null) return null;
        return new TextDecoder("utf-8").decode(decoded);
    } catch {
        return null;
    }
}

/**
 * Standard base64 decode → bytes. Implemented without `atob` to honor
 * the auth-namespace decode prohibition's spirit project-wide.
 *
 * Algorithm: classic 4-char groups → 3-byte chunks via the alphabet
 * lookup table.
 */
function decodeBase64Standard(b64: string): Uint8Array | null {
    const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    const lookup = new Map<string, number>();
    for (let i = 0; i < ALPHABET.length; i++) lookup.set(ALPHABET[i]!, i);

    let cleaned = "";
    for (const ch of b64) {
        if (ch === "=") break;
        if (!lookup.has(ch)) return null;
        cleaned += ch;
    }
    const groups = Math.ceil(cleaned.length / 4);
    const out = new Uint8Array(Math.floor((cleaned.length * 3) / 4));
    let outIdx = 0;
    for (let g = 0; g < groups; g++) {
        const c0 = lookup.get(cleaned[g * 4]!);
        const c1 = lookup.get(cleaned[g * 4 + 1]!);
        const c2 = cleaned[g * 4 + 2] ? lookup.get(cleaned[g * 4 + 2]!) : undefined;
        const c3 = cleaned[g * 4 + 3] ? lookup.get(cleaned[g * 4 + 3]!) : undefined;
        if (c0 === undefined || c1 === undefined) break;
        out[outIdx++] = (c0 << 2) | (c1 >> 4);
        if (c2 !== undefined) out[outIdx++] = ((c1 & 0xf) << 4) | (c2 >> 2);
        if (c3 !== undefined) out[outIdx++] = ((c2! & 0x3) << 6) | c3;
    }
    return out.subarray(0, outIdx);
}
